import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import type { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useAppSelector } from "../../redux/hooks";
import axiosInstance from "../../api/axiosInstance";
import { getAllOrders, createOrder } from "../../api/orderService";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  Plus, 
  Minus, 
  Trash2, 
  Save, 
  X, 
  Mail,
  User, 
  Phone, 
  MapPin, 
  Package, 
  Truck,
  Calendar,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Map,
  FileText,
  Printer,
  QrCode,
  User2,
} from "lucide-react";
import { toast } from "sonner";
import { getStatusColor, getStatusIcon, getStatusLabel } from "../../utils/statusUtils";

// Fix Leaflet icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface Medicine {
  id: string;
  name: string;
  potency?: string;
  quantity: number;
  price?: number;
  subtotal?: number;
  copay?: string;
}

// interface RiderDetail {
//   name: string;
//   phone: string;
//   location: string;
// }

interface OrderFormData {
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  customerEmail;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  } | string;
  priority: 'same-day' | 'next-day' | '';
  medicines: Medicine[];
  // riderDetail: RiderDetail;
  // deliveryCharges: number;
  deliveryDate: string;
  // totalPrice: number;
  notes: string; // Optional field for special instructions
  documents: string[]; // Array of selected document types
  delivery?: {
    status?: String,
    deliveryDate?: Date,
    deliveryTime?: String,
    deliveryType?: String,
    priority?: String
  },
}

// Location search result interface
interface LocationSuggestion {
  place_id: string;
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    road?: string;
    suburb?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

// Map click handler component with reverse geocoding
function LocationMarker({ 
  position, 
  setPosition,
  onAddressFound 
}: { 
  position: LatLngExpression | null; 
  setPosition: (pos: LatLngExpression) => void;
  onAddressFound?: (address: string) => void;
}) {
  useMapEvents({
    async click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
      
      // Reverse geocode to get address
      if (onAddressFound) {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${e.latlng.lat}&lon=${e.latlng.lng}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'CourierRX'
              }
            }
          );
          const data = await response.json();
          if (data.display_name) {
            onAddressFound(data.display_name);
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
        }
      }
    },
  });

  return position === null ? null : <Marker position={position} />;
}

// Map center updater component
function MapCenterUpdater({ center }: { center: LatLngExpression }) {
  const map = useMap();
  
  React.useEffect(() => {
    map.setView(center, 13);
  }, [center, map]);
  
  return null;
}

export function OrderManagementPage() {
  const user = useAppSelector((state) => state.auth.user);
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  
  // Map modal state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapModalMode, setMapModalMode] = useState<'create' | 'edit'>('create'); // Track if map is for create or edit
  const [selectedPosition, setSelectedPosition] = useState<LatLngExpression | null>([40.0583, -74.4057]); // Default: New Jersey
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([40.0583, -74.4057]); // Default: New Jersey
  const [tempAddress, setTempAddress] = useState('Trenton, New Jersey, USA');
  const [addressSuggestions, setAddressSuggestions] = useState<LocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [formData, setFormData] = useState<OrderFormData>({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    customerEmail: '',
    priority: 'next-day', // Default to next-day delivery
    delivery: {},
    location: '',
    medicines: [],
    // riderDetail: {
    //   name: '',
    //   phone: '',
    //   location: ''
    // },
    // deliveryCharges: 0,
    deliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow by default
    // totalPrice: 0,
    notes: '',
    documents: ['Delivery Slip'] // Default to Delivery Slip checked
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  // View and Edit dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<any>(null);

  // QR Code modal state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [createdOrderData, setCreatedOrderData] = useState<any>(null);

  // Search for address suggestions using Nominatim (OpenStreetMap)
  const searchAddress = async (query: string) => {
    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'CourierRX'
          }
        }
      );
      const data = await response.json();
      setAddressSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
      setAddressSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle address input change with debouncing
  const handleAddressChange = (value: string) => {
    setTempAddress(value);
    
    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchAddress(value);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Select a suggestion
  const handleSuggestionSelect = (suggestion: LocationSuggestion) => {
    setTempAddress(suggestion.display_name);
    setSelectedPosition([parseFloat(suggestion.lat), parseFloat(suggestion.lon)]);
    setMapCenter([parseFloat(suggestion.lat), parseFloat(suggestion.lon)]);
    setShowSuggestions(false);
    setAddressSuggestions([]);
    toast.success('Location selected from suggestions');
  };

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    // toast.info('Getting your location...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter([latitude, longitude]);
        setSelectedPosition([latitude, longitude]);
        
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'CourierRX'
              }
            }
          );
          const data = await response.json();
          if (data.display_name) {
            setTempAddress(data.display_name);
          }
          // toast.success('Location detected successfully');
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          // toast.success('Location detected');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get your location. Please allow location access.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Handle map modal open - get user location on open
  const handleMapModalOpen = (mode: 'create' | 'edit' = 'create') => {
    setMapModalMode(mode);
    setMapModalOpen(true);
    getUserLocation();
  };

  // Fetch existing orders
  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      setOrdersError(null);
      
      // console.log('📋 OrderManagementPage: Fetching orders using unified service...');
      const { orders } = await getAllOrders();
      
      // console.log('✅ OrderManagementPage: Orders fetched from unified service', {
      //   count: orders.length
      // });
      // console.log(orders)
      setOrders(orders);
    } catch (err: any) {
      console.error('❌ OrderManagementPage: Error fetching orders:', err);
      setOrdersError(err.message || 'Failed to fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  // Fetch orders on component mount and when switching to orders tab
  useEffect(() => {
    if (activeTab === "orders") {
      fetchOrders();
    }
  }, [activeTab]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#map-address') && !target.closest('.address-suggestions')) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  // Reset map state when modal closes
  useEffect(() => {
    if (!mapModalOpen) {
      setShowSuggestions(false);
      setAddressSuggestions([]);
    }
  }, [mapModalOpen]);

  // Calculate total price whenever medicines or delivery charges change
  useEffect(() => {
    const medicinesTotal = formData.medicines.reduce((sum, medicine) => sum + medicine.subtotal, 0);
    const total = medicinesTotal;
    setFormData(prev => ({ ...prev, totalPrice: total }));
  }, [formData.medicines]);

  // Auto-update delivery date when priority changes
  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setFormData(prev => ({
      ...prev,
      deliveryDate: prev.priority === 'same-day' 
        ? today.toISOString().split('T')[0] 
        : tomorrow.toISOString().split('T')[0]
    }));
  }, [formData.priority]);

  const addMedicine = () => {
    const newMedicine: Medicine = {
      id: Date.now().toString(),
      name: '',
      potency: '',
      quantity: 1,
      price: 0,
      subtotal: 0
    };
    setFormData(prev => ({
      ...prev,
      medicines: [...prev.medicines, newMedicine]
    }));
  };

  const updateMedicine = (id: string, field: keyof Medicine, value: any) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.map(medicine => {
        if (medicine.id === id) {
          const updated = { ...medicine, [field]: value };
          // if (field === 'quantity' || field === 'price') {
          //   updated.subtotal = updated.quantity * updated.price;
          // }
          return updated;
        }
        return medicine;
      })
    }));
  };

  const removeMedicine = (id: string) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.filter(medicine => medicine.id !== id)
    }));
  };

  const validateForm = (): boolean => {
    // if (!formData.customerName.trim()) {
    //   setError('Customer name is required');
    //   return false;
    // }
    // if (!formData.customerPhone.trim()) {
    //   setError('Customer phone is required');
    //   return false;
    // }
    // if (!formData.customerAddress.trim()) {
    //   setError('Customer address is required');
    //   return false;
    // }
    if (!formData.location || (typeof formData.location === 'string' && !formData.location.trim()) || 
        (typeof formData.location === 'object' && !formData.location.latitude)) {
      setError('Please select delivery location on map');
      return false;
    }
    // if (formData.medicines.length === 0) {
    //   setError('At least one medicine is required');
    //   return false;
    // }
    
    // if (formData.medicines.some(medicine => !medicine.name.trim() || !medicine.potency.trim())) {
    //   setError('All medicines must have name and potency');
    //   return false;
    // }
    // if (!formData.riderDetail.name.trim()) {
    //   setError('Rider name is required');
    //   return false;
    // }
    // if (!formData.riderDetail.phone.trim()) {
    //   setError('Rider phone is required');
    //   return false;
    // }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Create payload matching exact backend expectations
      const orderData = {
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        customerEmail: formData.customerEmail,
        priority: formData.priority,
        location: formData.location,
        medicines: formData.medicines,
        // deliveryCharges: formData.deliveryCharges,
        deliveryDate: formData.deliveryDate, // Use the selected date from form
        // totalPrice: formData.totalPrice,
        documents: formData.documents, // Include selected documents
        pharmacyId: user?.id, // Auto-filled from JWT on backend
        pharmacyName: user?.pharmacyName, // Auto-filled from JWT on backend
        pharmacyPhone: user?.phone,
        pharmacyEmail: user?.email,
        pharmacyAddress: user?.address?.streetAddress || user?.location?.address,
        ...(formData.notes.trim() && { notes: formData.notes.trim() }) // Only include if not empty
      };

      console.log('➕ OrderManagementPage: Creating order using unified service:', orderData);
      
      const { order, success: createSuccess } = await createOrder(orderData);
      
      console.log('✅ OrderManagementPage: Order created successfully:', order);
      
      if (createSuccess && order) {
        setSuccess(true);
        
        // Store the created order data
        setCreatedOrderData(order);
        
        // Show QR code modal instead of immediately switching tabs
        setQrModalOpen(true);
        
        // Show order number in success message if available
        const orderNumber = order.orderNumber;
        if (orderNumber) {
          toast.success(`Order created successfully! Order Number: ${orderNumber}`);
          console.log(`Order created successfully with number: ${orderNumber}`);
        } else {
          setSuccessMessage('Order created successfully!');
        }
        
        // Reset form
        setFormData({
          customerName: '',
          customerPhone: '',
          customerAddress: '',
          customerEmail: '',
          priority: 'next-day', // Default to next-day delivery
          delivery: {},
          location: '',
          medicines: [],
          // riderDetail: {
          //   name: '',
          //   phone: '',
          //   location: ''
          // },
          // deliveryCharges: 0,
          deliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
          // totalPrice: 0,
          notes: '',
          documents: ['Delivery Slip'] // Reset to default
        });
      } else {
        setError('Failed to create order');
      }
    } catch (err: any) {
      console.error('Error creating order:', err);
      console.error('Error response:', err.response?.data);
      
      // Handle backend validation errors
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorMessages = Object.values(errors).join(', ');
        setError(`Validation failed: ${errorMessages}`);
      } else {
        setError(err.response?.data?.message || 'Failed to create order');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerPhone: '',
      customerAddress: '',
      customerEmail: '',
      location: '',
      priority: 'next-day', // Default to next-day delivery
      medicines: [],
      deliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow
      delivery: {},
      // totalPrice: 0,
      notes: '',
      documents: ['Delivery Slip'] // Reset to default
    });
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
  };

  // Status functions are now imported from utils/statusUtils.tsx

  // Handle view order
  const handleViewOrder = (order: any) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  // Handle edit order
  const handleEditOrder = (order: any) => {
    setSelectedOrder(order);
    setEditFormData({
      customerName: order.customerName,
      customerEmail: order.customerEmail,
      customerPhone: order.customerPhone,
      customerAddress: order.customerAddress,
      location: order.location,
      priority: order.priority || 'next-day',
      medicines: order.medicines || [],
      deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '',
      totalPrice: order.totalPrice,
      notes: order.notes || '',
      documents: order.documents || ['Delivery Slip'], // Include documents
      delivery: order.delivery || {}
    });
    setEditDialogOpen(true);
  };

  // Medicine management functions for Edit Order
  const addEditMedicine = () => {
    const newMedicine: Medicine = {
      id: Date.now().toString(),
      name: '',
      potency: '',
      quantity: 1,
      price: 0,
      subtotal: 0
    };
    setEditFormData(prev => ({
      ...prev,
      medicines: [...(prev.medicines || []), newMedicine]
    }));
  };

  const updateEditMedicine = (id: string, field: keyof Medicine, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      medicines: (prev.medicines || []).map(medicine => {
        if (medicine.id === id) {
          const updated = { ...medicine, [field]: value };
          return updated;
        }
        return medicine;
      })
    }));
  };

  const removeEditMedicine = (id: string) => {
    setEditFormData(prev => ({
      ...prev,
      medicines: (prev.medicines || []).filter(medicine => medicine.id !== id)
    }));
  };

  // Save edited order
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.patch(`/orders/${selectedOrder._id || selectedOrder.id}`, editFormData);
      
      if (response.data.success) {
        // Update local state
        setOrders(prev => prev.map(o => 
          (o._id || o.id) === (selectedOrder._id || selectedOrder.id) 
            ? { ...o, ...editFormData }
            : o
        ));
        setEditDialogOpen(false);
        setSelectedOrder(null);
        setEditFormData(null);
        setSuccess(true);
        setSuccessMessage('Order updated successfully!');
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Error updating order:', err);
      setError(err.response?.data?.message || 'Failed to update order');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete order
  const handleDeleteOrder = (order: any) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  // Confirm delete order
  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/orders/${orderToDelete._id || orderToDelete.id}`);
      
      if (response.data.success) {
        // Remove from local state
        setOrders(prev => prev.filter(o => 
          (o._id || o.id) !== (orderToDelete._id || orderToDelete.id)
        ));
        setDeleteDialogOpen(false);
        setOrderToDelete(null);
        setSuccess(true);
        setSuccessMessage('Order deleted successfully!');
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Error deleting order:', err);
      setError(err.response?.data?.message || 'Failed to delete order');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'same-day':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'next-day':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Handle QR modal close - switch to orders tab and refresh
  const handleQrModalClose = () => {
    setQrModalOpen(false);
    setCreatedOrderData(null);
    setActiveTab("orders");
    // Refresh orders list
    fetchOrders();
  };

  // Print QR code
  const handlePrintQR = () => {
    if (!createdOrderData?.qrCode) {
      toast.error('QR code not available');
      return;
    }

    // Check if QR code is a valid image (base64 data URL)
    if (!createdOrderData.qrCode.startsWith('data:image')) {
      toast.error('QR code is still being generated. Please wait a moment and try again.');
      return;
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow pop-ups to print');
      return;
    }

    // Write the HTML content with QR code
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print QR Code - ${createdOrderData.orderNumber}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .qrcode-container {
              text-align: center;
              border: 2px solid #000;
              padding: 30px;
              background: white;
              max-width: 500px;
              width: 100%;
            }
            .qrcode-header {
              margin-bottom: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .qrcode-header h2 {
              margin: 5px 0;
              font-size: 20px;
            }
            .qrcode-footer {
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid #ccc;
            }
            .qrcode-footer p {
              margin: 5px 0;
              font-size: 14px;
              color: #333;
            }
            .qrcode-image {
              margin: 20px auto;
              border: 1px solid #ccc;
              display: block;
              width: 350px;
              height: 350px;
              object-fit: contain;
            }
            @media print {
              @page {
                margin: 0.5in;
                size: A4;
              }
              body {
                background: white !important;
                margin: 0 !important;
                padding: 0 !important;
                height: auto !important;
                display: block !important;
              }
              .qrcode-container {
                border: none !important;
                padding: 0 !important;
                margin: 0 auto !important;
                max-width: 100% !important;
                page-break-inside: avoid;
              }
              .qrcode-header h2 {
                font-size: 24px !important;
              }
              .qrcode-image {
                width: 400px !important;
                height: 400px !important;
                margin: 20px auto !important;
              }
              .qrcode-header-content-customer {
                display: flex;
                flex-direction: column;
                align-items: start;
                justify-content: start;
              }
            }
          </style>
        </head>
        
        <body>
          <div class="qrcode-container">
            <div class="qrcode-header">
              <div class="qrcode-header-content-customer">
                <p>👤<strong>Customer:</strong> ${createdOrderData.customerName || 'Customer'}</p>
                <p>📦<strong>Order Number:</strong> ${createdOrderData.orderNumber || 'Order Number'}</p>
              </div>
            </div>
            <img class="qrcode-image" src="${createdOrderData.qrCode}" alt="QR Code" />
            <div class="qrcode-footer">
              <p><strong>Website:</strong> https://courier-rx.com</p>
              <p><strong>Support:</strong>+1 862 295 8034</p>
            </div>
          </div>
          <script>
            // Auto print after a short delay
            setTimeout(() => {
              window.print();
            }, 500);
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
    toast.success('Opening print dialog...');
  };

  // Filter orders based on search, status, and priority
  const filteredOrders = orders.filter((order: any) => {
    // Search filter
    const searchMatch = !searchTerm || (
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Status filter
    const statusMatch = statusFilter === 'all' || order.pickupStatus === statusFilter || order.status === statusFilter;

    // Priority filter
    const priorityMatch = priorityFilter === 'all' || order.priority === priorityFilter;

    return searchMatch && statusMatch && priorityMatch;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600">Create and manage orders for {user?.pharmacyName}</p>
        </div>
        <div className="flex items-center space-x-2">
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="create" 
          // className="flex items-center space-x-2"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Plus className="w-4 h-4" />
            <span>Create Order</span>
          </TabsTrigger>
          <TabsTrigger value="orders" 
          // className="flex items-center space-x-2"
          className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-primary/20 transition-all duration-200 hover:bg-white/50"
          >
            <Package className="w-4 h-4" />
            <span>View Orders</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          )}

          {/* {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800">{successMessage || 'Order created successfully!'}</span>
            </div>
          )} */}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>Customer Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name</Label>
                <Input
                  id="customerName"
                  value={formData.customerName}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerEmail">Customer Email</Label>
                <Input
                  id="customerEmail"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  placeholder="Enter Customer Email"
                  type="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerPhone">Phone Number</Label>
                <Input
                  id="customerPhone"
                  value={formData.customerPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="Enter phone number"
                  type="tel"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Delivery Location</Label>
              <div className="flex gap-2">
                <Input
                  id="location"
                  value={
                    typeof formData.location === 'string' 
                      ? formData.location 
                      : formData.location.address
                  }
                  placeholder="Click map icon to select location"
                  readOnly
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (typeof formData.location !== 'string' && formData.location.latitude) {
                      setSelectedPosition([formData.location.latitude, formData.location.longitude]);
                      setTempAddress(formData.location.address);
                    } else {
                      setSelectedPosition(null);
                      setTempAddress('');
                    }
                    handleMapModalOpen('create');
                  }}
                  className="shrink-0"
                >
                  <Map className="w-4 h-4 mr-2" />
                  Select on Map
                </Button>
              </div>
              {typeof formData.location !== 'string' && formData.location.latitude && (
                <p className="text-xs text-gray-500 mt-1">
                  📍 Coordinates: {formData.location.latitude.toFixed(6)}, {formData.location.longitude.toFixed(6)}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority">Delivery Priority *</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value: 'same-day' | 'next-day') => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same-day">Same Day Delivery</SelectItem>
                    <SelectItem value="next-day">Next Day Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryDate">Delivery Date *</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="deliveryDate"
                    type="date"
                    value={formData.deliveryDate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                    className="pl-10 h-11"
                    required
                    min={new Date().toISOString().split('T')[0]} // Don't allow past dates
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Medicines */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Package className="w-5 h-5" />
                <span>Medicines</span>
              </CardTitle>
              <Button type="button" onClick={addMedicine} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Medicine
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.medicines.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No medicines added yet. Click "Add Medicine" to get started.</p>
              </div>
            ) : (
              formData.medicines.map((medicine, index) => (
                <div key={medicine.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Medicine {index + 1}</h4>
                    <Button
                      type="button"
                      onClick={() => removeMedicine(medicine.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label>RX</Label>
                      <Input
                        value={medicine.name}
                        onChange={(e) => updateMedicine(medicine.id, 'name', e.target.value)}
                        placeholder="Enter RX..."
                      />
                    </div>
                    {/* <div className="space-y-2">
                      <Label>Potency *</Label>
                      <Input
                        value={medicine.potency}
                        onChange={(e) => updateMedicine(medicine.id, 'potency', e.target.value)}
                        placeholder="e.g., 500mg, 10ml"
                      />
                    </div> */}

                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <div className="flex items-center space-x-2">
                        <Button
                          type="button"
                          onClick={() => updateMedicine(medicine.id, 'quantity', Math.max(1, medicine.quantity - 1))}
                          variant="outline"
                          size="sm"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <Input
                          type="number"
                          min="1"
                          value={medicine.quantity}
                          onChange={(e) => updateMedicine(medicine.id, 'quantity', parseInt(e.target.value) || 1)}
                          className="text-center"
                        />
                        <Button
                          type="button"
                          onClick={() => updateMedicine(medicine.id, 'quantity', medicine.quantity + 1)}
                          variant="outline"
                          size="sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`copay-${medicine.id}`}>Copay</Label>
                      <Input
                        id={`copay-${medicine.id}`}
                        value={medicine.copay || ""}
                        onChange={(e) => updateMedicine(medicine.id, 'copay', e.target.value)}
                        placeholder="Enter copay"
                      />
                    </div>
                    
                    {/* <div className="space-y-2">
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={medicine.price}
                        onChange={(e) => updateMedicine(medicine.id, 'price', parseFloat(e.target.value) || 0)}
                        placeholder="0.00"
                      />
                    </div> */}
                    {/* <div className="space-y-2">
                      <Label>Subtotal ($)</Label>
                      <div className="flex items-center h-10 px-3 border border-gray-300 rounded-md bg-gray-50">
                        <span className="text-gray-700 font-medium">{medicine.subtotal.toFixed(2)}</span>
                      </div>
                    </div> */}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Documents Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Documents</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">Select the documents to include with this order (at least one must be selected):</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Delivery Slip - Checked by default */}
                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    id="doc-delivery-slip"
                    checked={formData.documents.includes('Delivery Slip')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ 
                          ...prev, 
                          documents: [...prev.documents, 'Delivery Slip'] 
                        }));
                      } else {
                        // Ensure at least one document is selected
                        if (formData.documents.length > 1) {
                          setFormData(prev => ({ 
                            ...prev, 
                            documents: prev.documents.filter(d => d !== 'Delivery Slip') 
                          }));
                        } else {
                          toast.error('At least one document must be selected');
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="doc-delivery-slip" className="flex-1 text-sm font-medium text-gray-900 cursor-pointer">
                    Delivery Slip
                  </label>
                </div>

                {/* Hippa */}
                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    id="doc-hippa"
                    checked={formData.documents.includes('Hippa')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ 
                          ...prev, 
                          documents: [...prev.documents, 'Hippa'] 
                        }));
                      } else {
                        // Ensure at least one document is selected
                        if (formData.documents.length > 1) {
                          setFormData(prev => ({ 
                            ...prev, 
                            documents: prev.documents.filter(d => d !== 'Hippa') 
                          }));
                        } else {
                          toast.error('At least one document must be selected');
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="doc-hippa" className="flex-1 text-sm font-medium text-gray-900 cursor-pointer">
                    Hippa
                  </label>
                </div>

                {/* Lien */}
                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    id="doc-lien"
                    checked={formData.documents.includes('Lien')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ 
                          ...prev, 
                          documents: [...prev.documents, 'Lien'] 
                        }));
                      } else {
                        // Ensure at least one document is selected
                        if (formData.documents.length > 1) {
                          setFormData(prev => ({ 
                            ...prev, 
                            documents: prev.documents.filter(d => d !== 'Lien') 
                          }));
                        } else {
                          toast.error('At least one document must be selected');
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="doc-lien" className="flex-1 text-sm font-medium text-gray-900 cursor-pointer">
                    Lien
                  </label>
                </div>

                {/* AOB */}
                <div className="flex items-center space-x-3 p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    id="doc-aob"
                    checked={formData.documents.includes('AOB')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ 
                          ...prev, 
                          documents: [...prev.documents, 'AOB'] 
                        }));
                      } else {
                        // Ensure at least one document is selected
                        if (formData.documents.length > 1) {
                          setFormData(prev => ({ 
                            ...prev, 
                            documents: prev.documents.filter(d => d !== 'AOB') 
                          }));
                        } else {
                          toast.error('At least one document must be selected');
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="doc-aob" className="flex-1 text-sm font-medium text-gray-900 cursor-pointer">
                    AOB
                  </label>
                </div>
              </div>
              {formData.documents.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Selected documents:</strong> {formData.documents.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rider Details */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Truck className="w-5 h-5" />
              <span>Rider Details</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="riderName">Rider Name *</Label>
                <Input
                  id="riderName"
                  value={formData.riderDetail.name}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    riderDetail: { ...prev.riderDetail, name: e.target.value }
                  }))}
                  placeholder="Enter rider name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="riderPhone">Rider Phone *</Label>
                <Input
                  id="riderPhone"
                  value={formData.riderDetail.phone}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    riderDetail: { ...prev.riderDetail, phone: e.target.value }
                  }))}
                  placeholder="Enter rider phone"
                  type="tel"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="riderLocation">Rider Location</Label>
              <Input
                id="riderLocation"
                value={formData.riderDetail.location}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  riderDetail: { ...prev.riderDetail, location: e.target.value }
                }))}
                placeholder="Enter rider location"
              />
            </div>
          </CardContent>
        </Card> */}

        {/* Delivery Information */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Delivery Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deliveryDate">Delivery Date *</Label>
                <Input
                  id="deliveryDate"
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryTime">Delivery Time *</Label>
                <Input
                  id="deliveryTime"
                  type="time"
                  value={formData.deliveryTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryTime: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryCharges">Delivery Charges ($)</Label>
                <Input
                  id="deliveryCharges"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.deliveryCharges}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryCharges: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
            </div>
          </CardContent>
        </Card> */}

        {/* Special Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Package className="w-5 h-5" />
              <span>Special Instructions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter any special instructions or notes for this order..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Order Summary */}
        {/* <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5" />
              <span>Order Summary</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Medicines Total:</span>
                <span className="font-medium">${formData.medicines.reduce((sum, medicine) => sum + medicine.subtotal, 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Delivery Charges:</span>
                <span className="font-medium">${formData.deliveryCharges.toFixed(2)}</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Price:</span>
                  <span className="text-xl font-bold text-green-600">${formData.totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card> */}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          {/* <Button type="button" onClick={resetForm} variant="outline">
            Reset Form
          </Button> */}
          
          <Button type="submit" disabled={loading} variant="outline" className="min-w-[120px] ">
            <Save className="w-4 h-4 mr-2" />
            Create Order
          </Button>
        </div>
      </form>
        </TabsContent>

        <TabsContent value="orders" className="space-y-6">
          {ordersError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{ordersError}</span>
            </div>
          )}

          {ordersLoading ? (
            <GlobalLoader 
              isLoading={true} 
              message="Loading orders..." 
              subMessage="Please wait while we fetch your orders" 
            />
          ) : (
            <>
              {/* Filters Card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                      <Input
                        placeholder="Search by order number, customer name, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-11"
                      />
                    </div>
                    {/* <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Filter by Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="preparing">Preparing</SelectItem>
                        <SelectItem value="ready-for-pickup">Ready for Pickup</SelectItem>
                        <SelectItem value="picked-up">Picked Up</SelectItem>
                        <SelectItem value="in-transit">In Transit</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select> */}
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Filter by Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="same-day">Same Day</SelectItem>
                        <SelectItem value="next-day">Next Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
              <CardHeader>
                <CardTitle>Orders ({filteredOrders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
                    <p className="text-gray-500">
                      {orders.length === 0 ? 'Create your first order to get started.' : 'No orders match your filters.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredOrders.map((order: any) => (
                      <div key={order._id || order.id} className="border rounded-lg p-5 hover:shadow-md transition-shadow bg-white">
                        {/* Header Section */}
                        <div className="flex items-start justify-between mb-4 pb-4 border-b">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {order.orderNumber || `Order #${order._id?.slice(-6) || order.id?.slice(-6)}`}
                              </h3>
                              <Badge className={getStatusColor(order.pickupStatus || order.status)}>
                                {order.pickupStatus || order.status}
                              </Badge>
                              {order.priority && (
                                <Badge variant="outline" className={getPriorityColor(order.priority)}>
                                  {order.priority === 'same-day' ? 'Same Day' : 'Next Day'}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleViewOrder(order)}>
                              <Eye className="w-4 h-4 mr-1" />
                              View Details
                            </Button>
                            {(order.pickupStatus || order.status) === 'pending' && (
                              <>
                                <Button variant="outline" size="sm" onClick={() => handleEditOrder(order)}>
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleDeleteOrder(order)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Main Content Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
                          {/* Customer Info */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-gray-700">Customer</span>
                            </div>
                            <p className="font-medium text-gray-900">{order.customerName || 'N/A'}</p>
                            {order.customerPhone && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Phone className="w-3 h-3" />
                                <span>{order.customerPhone}</span>
                              </div>
                            )}
                            {order.customerEmail && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Mail className="w-3 h-3" />
                                <span className="truncate">{order.customerEmail}</span>
                              </div>
                            )}
                          </div>

                          {/* Delivery Info */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-gray-700">Delivery Location</span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2">
                              {typeof order.location === 'object' && order.location?.address
                                ? order.location.address
                                : typeof order.location === 'string'
                                ? order.location
                                : 'Location not specified'}
                            </p>
                            {order.deliveryDate && (
                              <div className="flex items-center gap-1 text-sm text-gray-600 mt-2">
                                <Calendar className="w-3 h-3" />
                                Delivery Date: 
                                <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>

                          {/* Order Summary */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-gray-700">Order Summary</span>
                            </div>
                            <p className="text-sm text-gray-600">{order.medicines?.length || 0} medicine(s)</p>
                            {/* {order.totalPrice !== undefined && order.totalPrice !== null && (
                              <p className="text-lg font-semibold text-green-600">${order.totalPrice.toFixed(2)}</p>
                            )} */}
                          </div>
                        </div>
                        
                        {/* Medicines Preview */}
                        {order.medicines && order.medicines.length > 0 && (
                          <div className="pt-4 border-t">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Medicines</p>
                            <div className="flex flex-wrap gap-2">
                              {order.medicines.slice(0, 4).map((medicine: any, index: number) => (
                                <Badge key={index} variant="secondary" className="text-xs">
                                  {medicine.name} {medicine.quantity ? `(×${medicine.quantity})` : ''}
                                </Badge>
                              ))}
                              {order.medicines.length > 4 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{order.medicines.length - 4} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Special Notes */}
                        {order.notes && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs font-medium text-gray-500 uppercase mb-1">Special Instructions</p>
                            <p className="text-sm text-gray-700 line-clamp-2">{order.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* View Order Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl">Order Details</DialogTitle>
            <DialogDescription>
              Complete information for {selectedOrder?.orderNumber || 'this order'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-5">
              {/* Order Header with Status */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedOrder.orderNumber || `Order #${selectedOrder._id?.slice(-6) || selectedOrder.id?.slice(-6)}`}
                  </h3>
                  {selectedOrder.priority && (
                    <p className="text-sm text-gray-600 mt-1">
                      Priority: <span className="font-medium capitalize">{selectedOrder.priority}</span>
                    </p>
                  )}
                </div>
                <Badge className={`${getStatusColor(selectedOrder.pickupStatus || selectedOrder.status)} text-sm px-3 py-1`}>
                  {selectedOrder.pickupStatus || selectedOrder.status}
                </Badge>
              </div>

              {/* Customer Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Customer Information</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 uppercase mb-1">Name</p>
                    <p className="font-medium text-gray-900">{selectedOrder.customerName || 'N/A'}</p>
                  </div>
                  {selectedOrder.customerPhone && (
                    <div>
                      <p className="text-xs text-gray-600 uppercase mb-1">Phone</p>
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <p className="font-medium text-gray-900">{selectedOrder.customerPhone}</p>
                      </div>
                    </div>
                  )}
                  {selectedOrder.customerEmail && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-600 uppercase mb-1">Email</p>
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <p className="font-medium text-gray-900">{selectedOrder.customerEmail}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Delivery Location */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Delivery Location</h4>
                </div>
                <p className="text-sm text-gray-900 mb-2">
                  {typeof selectedOrder.location === 'object' && selectedOrder.location?.address
                    ? selectedOrder.location.address
                    : typeof selectedOrder.location === 'string'
                    ? selectedOrder.location
                    : 'Location not specified'}
                </p>
                {typeof selectedOrder.location === 'object' && selectedOrder.location?.latitude && (
                  <p className="text-xs text-gray-600">
                    📍 Coordinates: {selectedOrder.location.latitude.toFixed(6)}, {selectedOrder.location.longitude.toFixed(6)}
                  </p>
                )}
                {selectedOrder.deliveryDate && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-600 uppercase">Delivery Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(selectedOrder.deliveryDate).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents Section */}
                {selectedOrder.documents && selectedOrder.documents.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <p className="text-xs text-gray-600 uppercase mb-2">Selected Documents</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedOrder.documents.map((doc: string, index: number) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          <FileText className="w-3 h-3 mr-1" />
                          {doc}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Medicines List */}
              {selectedOrder.medicines && selectedOrder.medicines.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">Medicines ({selectedOrder.medicines.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedOrder.medicines.map((medicine: any, index: number) => (
                      <div key={index} className="flex items-center justify-between bg-white rounded p-3">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{medicine.name || 'N/A'}</p>
                          {medicine.copay && (
                            <p className="text-xs text-gray-600 mt-1">Copay: {medicine.copay}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">Qty: {medicine.quantity || 1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              {selectedOrder.notes && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <h4 className="font-semibold text-gray-900">Special Instructions</h4>
                  </div>
                  <p className="text-sm text-gray-900">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Order Summary */}
              {/* {selectedOrder.totalPrice !== undefined && selectedOrder.totalPrice !== null && (
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-900">Total Amount</h4>
                    </div>
                    <p className="text-2xl font-bold text-green-600">${selectedOrder.totalPrice.toFixed(2)}</p>
                  </div>
                </div>
              )} */}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Order</DialogTitle>
            <DialogDescription>
              Update order details for {selectedOrder?.orderNumber || 'this order'}
            </DialogDescription>
          </DialogHeader>
          
          {editFormData && (
            <div className="space-y-5">
              {/* Customer Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Customer Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-customerName">Customer Name</Label>
                      <Input
                        id="edit-customerName"
                        value={editFormData.customerName}
                        onChange={(e) => setEditFormData({ ...editFormData, customerName: e.target.value })}
                        placeholder="Enter customer name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-customerEmail">Customer Email</Label>
                      <Input
                        id="edit-customerEmail"
                        value={editFormData.customerEmail || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, customerEmail: e.target.value })}
                        placeholder="Enter Customer Email"
                        type="email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-customerPhone">Phone Number</Label>
                      <Input
                        id="edit-customerPhone"
                        value={editFormData.customerPhone}
                        onChange={(e) => setEditFormData({ ...editFormData, customerPhone: e.target.value })}
                        placeholder="Enter phone number"
                        type="tel"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-location">Delivery Location</Label>
                    <div className="flex gap-2">
                      <Input
                        id="edit-location"
                        value={
                          typeof editFormData.location === 'string' 
                            ? editFormData.location 
                            : editFormData.location?.address || ''
                        }
                        placeholder="Click map icon to select location"
                        readOnly
                        required
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (typeof editFormData.location !== 'string' && editFormData.location?.latitude) {
                            setSelectedPosition([editFormData.location.latitude, editFormData.location.longitude]);
                            setTempAddress(editFormData.location.address);
                          } else {
                            setSelectedPosition(null);
                            setTempAddress('');
                          }
                          handleMapModalOpen('edit');
                        }}
                        className="shrink-0"
                      >
                        <Map className="w-4 h-4 mr-2" />
                        Select on Map
                      </Button>
                    </div>
                    {typeof editFormData.location !== 'string' && editFormData.location?.latitude && (
                      <p className="text-xs text-gray-500 mt-1">
                        📍 Coordinates: {editFormData.location.latitude.toFixed(6)}, {editFormData.location.longitude.toFixed(6)}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-priority">Delivery Priority *</Label>
                      <Select 
                        value={editFormData.priority} 
                        onValueChange={(value: 'same-day' | 'next-day') => setEditFormData(prev => ({ ...prev, priority: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="same-day">Same Day Delivery</SelectItem>
                          <SelectItem value="next-day">Next Day Delivery</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-deliveryDate">Delivery Date *</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="edit-deliveryDate"
                          type="date"
                          value={editFormData.deliveryDate || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                          className="pl-10 h-11"
                          required
                          min={new Date().toISOString().split('T')[0]} // Don't allow past dates
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Medicines Card - Fully Editable */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Package className="w-5 h-5" />
                      <span>Medicines</span>
                    </CardTitle>
                    <Button type="button" onClick={addEditMedicine} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Medicine
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!editFormData.medicines || editFormData.medicines.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No medicines added yet. Click "Add Medicine" to get started.</p>
                    </div>
                  ) : (
                    editFormData.medicines.map((medicine, index) => (
                      <div key={medicine.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-gray-900">Medicine {index + 1}</h4>
                          <Button
                            type="button"
                            onClick={() => removeEditMedicine(medicine.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                          <div className="space-y-2">
                            <Label>RX</Label>
                            <Input
                              value={medicine.name}
                              onChange={(e) => updateEditMedicine(medicine.id, 'name', e.target.value)}
                              placeholder="Enter medicine name"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Quantity</Label>
                            <div className="flex items-center space-x-2">
                              <Button
                                type="button"
                                onClick={() => updateEditMedicine(medicine.id, 'quantity', Math.max(1, medicine.quantity - 1))}
                                variant="outline"
                                size="sm"
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                min="1"
                                value={medicine.quantity}
                                onChange={(e) => updateEditMedicine(medicine.id, 'quantity', parseInt(e.target.value) || 1)}
                                className="text-center"
                              />
                              <Button
                                type="button"
                                onClick={() => updateEditMedicine(medicine.id, 'quantity', medicine.quantity + 1)}
                                variant="outline"
                                size="sm"
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor={`edit-copay-${medicine.id}`}>Copay</Label>
                            <Input
                              id={`edit-copay-${medicine.id}`}
                              value={medicine.copay || ""}
                              onChange={(e) => updateEditMedicine(medicine.id, 'copay', e.target.value)}
                              placeholder="Enter copay"
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Special Instructions Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Package className="w-5 h-5" />
                    <span>Special Instructions</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label htmlFor="edit-notes">Notes (Optional)</Label>
                    <Textarea
                      id="edit-notes"
                      value={editFormData.notes || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      placeholder="Enter any special instructions or notes for this order..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="flex justify-end space-x-4 mt-6">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={loading} className="min-w-[120px]">
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete order <strong>{orderToDelete?.orderNumber}</strong> for customer <strong>{orderToDelete?.customerName}</strong>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteOrder}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Map Location Selector Modal */}
      <Dialog open={mapModalOpen} onOpenChange={setMapModalOpen}>
        <DialogContent 
          className="!max-w-[95vw] !w-[1200px] max-h-[90vh] overflow-auto bg-white"
          style={{ maxWidth: '95vw', width: '1200px', backgroundColor: 'white' }}
        >
          <DialogHeader className="bg-white">
            <DialogTitle>Select Delivery Location</DialogTitle>
            <DialogDescription>
              Click on the map to select the delivery location. Enter the address below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 bg-white">
            {/* Address Input with Autocomplete */}
            <div className="space-y-2 bg-white relative">
              <div className="flex items-center justify-between">
                <Label htmlFor="map-address">Delivery Address *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={getUserLocation}
                  className="text-xs"
                >
                  <MapPin className="w-3 h-3 mr-1" />
                  Use My Location
                </Button>
              </div>
              <div className="relative">
                <Textarea
                  id="map-address"
                  value={tempAddress}
                  onChange={(e) => {
                    handleAddressChange(e.target.value);
                  }}
                  onFocus={() => {
                    if (addressSuggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="Start typing address... (e.g., Trenton, New Jersey)"
                  rows={2}
                  className="w-full bg-white"
                />
              </div>

              {/* Address Suggestions Dropdown */}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="address-suggestions absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto mt-1">
                  {addressSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.place_id}
                      type="button"
                      onClick={() => handleSuggestionSelect(suggestion)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-blue-600 mt-1 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {suggestion.display_name.split(',')[0]}
                          </p>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {suggestion.display_name}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Instructions:</strong> Type an address or click anywhere on the map to place a marker. The address will auto-fill when you click on the map.
              </p>
            </div>

            {/* Map Container */}
            <div className="h-[400px] w-full rounded-lg overflow-hidden border border-gray-200">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                className="z-0"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapCenterUpdater center={mapCenter} />
                <LocationMarker 
                  position={selectedPosition} 
                  setPosition={setSelectedPosition}
                  onAddressFound={(address) => {
                    setTempAddress(address);
                    setShowSuggestions(false);
                    // toast.success('Address auto-filled from map location');
                  }}
                />
              </MapContainer>
            </div>

            {/* Selected Coordinates Display */}
            {selectedPosition && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <strong>Selected Coordinates:</strong><br />
                  Latitude: {(selectedPosition as number[])[0].toFixed(6)}<br />
                  Longitude: {(selectedPosition as number[])[1].toFixed(6)}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 mt-4 bg-white pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMapModalOpen(false);
                setSelectedPosition(null);
                setTempAddress('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!selectedPosition) {
                  toast.error('Please select a location on the map');
                  return;
                }
                if (!tempAddress.trim()) {
                  toast.error('Please enter the delivery address');
                  return;
                }

                const locationData = {
                  latitude: (selectedPosition as number[])[0],
                  longitude: (selectedPosition as number[])[1],
                  address: tempAddress
                };

                // Update the appropriate form based on mode
                if (mapModalMode === 'edit') {
                  setEditFormData(prev => ({
                    ...prev,
                    location: locationData
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    location: locationData
                  }));
                }

                // toast.success('Location selected successfully');
                setMapModalOpen(false);
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Confirm Location
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={(open) => !open && handleQrModalClose()}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <QrCode className="w-6 h-6 text-green-600" />
              Order Created Successfully!
            </DialogTitle>
            <DialogDescription>
              Your order has been created. Scan the QR code below for quick access to order details.
            </DialogDescription>
          </DialogHeader>
          
          {createdOrderData && (
            <div className="space-y-6">
              {/* Order Info Banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Order #{createdOrderData.orderNumber}</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                  <p><strong>Customer:</strong> {createdOrderData.customerName || 'N/A'}</p>
                  <p><strong>Priority:</strong> {createdOrderData.priority === 'same-day' ? 'Same Day' : 'Next Day'}</p>
                  <p><strong>Medicines:</strong> {createdOrderData.medicines?.length || 0} item(s)</p>
                  <p><strong>Status:</strong> {createdOrderData.pickupStatus || createdOrderData.status}</p>
                </div>
              </div>

              {/* QR Code Display */}
              <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                {createdOrderData.qrCode ? (
                  <>
                    {createdOrderData.qrCode.startsWith('data:image') ? (
                      <img 
                        src={createdOrderData.qrCode} 
                        alt="Order QR Code" 
                        className="w-64 h-64 border-4 border-white rounded-lg shadow-lg bg-white p-4"
                      />
                    ) : (
                      <div className="text-center py-8">
                        <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600">QR code is being generated...</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Please refresh the page or check back later
                        </p>
                      </div>
                    )}
                    <p className="mt-4 text-sm text-gray-600 text-center">
                      Scan this QR code to track your order
                    </p>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-600">QR code not available</p>
                  </div>
                )}
              </div>

            </div>
          )}

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleQrModalClose}
              className="flex-1"
            >
              <X className="w-4 h-4 mr-2" />
              Close
            </Button>
            <Button
              onClick={() => {
                handlePrintQR();
                // Close modal after printing
                setTimeout(() => handleQrModalClose(), 500);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              disabled={!createdOrderData?.qrCode || !createdOrderData.qrCode.startsWith('data:image')}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global loader for main operations */}
      <GlobalLoader 
        isLoading={loading} 
        message="Processing..." 
        subMessage="Please wait while we process your request" 
      />
    </div>
  );
}
